import DeleteIcon from '@mui/icons-material/Delete'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { isRoleAuthorized } from 'common-billing-server/role'
import { RegisteredSshPublicKey, Role, UserManagementSystem } from 'common-billing-server/types'
import DataSet from 'common-ui/components/DataSet'
import { Banner } from 'common-ui/components/Banner'
import { PasskeyManager } from 'common-ui/components/PasskeyManager'
import { TotpManager } from 'common-ui/components/TotpManager'
import { usePasskeyEnrollmentPrompt } from 'common-ui/passkeys'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import Wrapper from '../common/Wrapper'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useAuth } from '../login/Auth'

export const ProfilePage = () => {
    const billingApi = useBillingApi()
    const { user, refreshUser } = useAuth()
    const promptPasskey = usePasskeyEnrollmentPrompt()
    const canManagePasskeys = user?.managedBy === UserManagementSystem.billingServer
    const canManageSshKeys = !!user && isRoleAuthorized({ role: user.role, minimumAllowedRole: Role.admin })
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [sshKey, setSshKey] = useState<RegisteredSshPublicKey | null>()
    const [publicKey, setPublicKey] = useState('')
    const [fileName, setFileName] = useState<string>()
    const [saving, setSaving] = useState(false)
    const [removing, setRemoving] = useState(false)
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [notice, setNotice] = useState<string>()
    const [mfaEnforced, setMfaEnforced] = useState<boolean>()

    useEffect(() => {
        let active = true
        const loadMfaPolicy = async () => {
            removeFailedOperation('load-mfa-policy')
            try {
                const settings = await billingApi.getGlobalsettings()
                if (active) setMfaEnforced(settings.mfaEnforced)
            } catch (error) {
                addFailedOperation({
                    id: 'load-mfa-policy',
                    message: 'The MFA policy could not be loaded.',
                    error: error as Error,
                })
            }
        }
        void loadMfaPolicy()
        return () => {
            active = false
        }
    }, [billingApi])

    const load = useCallback(async () => {
        if (!canManageSshKeys) return
        removeFailedOperation('load-ssh-key')
        try {
            const result = await billingApi.getCurrentUserSshKey()
            setSshKey(result.sshKey)
        } catch {
            addFailedOperation({
                id: 'load-ssh-key',
                message: 'Your SSH public key could not be loaded.',
                retryFn: () => void load(),
            })
        }
    }, [billingApi, canManageSshKeys])

    useEffect(() => {
        if (canManageSshKeys) void load()
    }, [canManageSshKeys, load])

    const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        setPublicKey(await file.text())
        setFileName(file.name)
        event.target.value = ''
    }

    const save = async () => {
        removeFailedOperation('save-ssh-key')
        setSaving(true)
        try {
            const registered = await billingApi.setCurrentUserSshKey({ publicKey, fileName })
            setSshKey(registered)
            setPublicKey('')
            setFileName(undefined)
            setNotice('Your SSH public key has been registered.')
        } catch (error) {
            addFailedOperation({
                id: 'save-ssh-key',
                message: 'The SSH public key could not be registered.',
                error: error as Error,
            })
        } finally {
            setSaving(false)
        }
    }

    const remove = async () => {
        removeFailedOperation('remove-ssh-key')
        setRemoving(true)
        try {
            await billingApi.deleteCurrentUserSshKeys()
            setSshKey(null)
            setRemoveDialogOpen(false)
            setNotice('Your SSH public key has been removed.')
        } catch (error) {
            addFailedOperation({
                id: 'remove-ssh-key',
                message: 'The SSH public key could not be removed.',
                error: error as Error,
            })
        } finally {
            setRemoving(false)
        }
    }

    let authenticationSettings = (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h2">Multi-factor authentication</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Your sign-in methods are managed by Microsoft Entra ID. Configure MFA or passkeys there.
            </Typography>
        </Paper>
    )
    if (canManagePasskeys) {
        authenticationSettings = (
            <>
                <TotpManager
                    api={billingApi.totp}
                    totpEnabled={user?.mfa?.totpEnabled ?? false}
                    mfaEnforced={mfaEnforced}
                    onChanged={refreshUser}
                />
                <PasskeyManager api={billingApi.passkeys} promptEnrollment={promptPasskey} />
            </>
        )
    }

    return (
        <Wrapper title="Profile">
            <Stack spacing={2} sx={{ maxWidth: 800 }}>
                {authenticationSettings}
                {canManageSshKeys && (
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="h2">SSH public key</Typography>
                                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                    Register one Ed25519, RSA, or ECDSA public key to request appliance-specific SSH
                                    certificates.
                                </Typography>
                            </Box>

                            {sshKey === undefined ? (
                                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 120 }}>
                                    <CircularProgress aria-label="Loading SSH public key" />
                                </Box>
                            ) : sshKey ? (
                                <DataSet
                                    values={{
                                        Fingerprint: sshKey.fingerprint,
                                        File: sshKey.fileName,
                                        'Last registered (UTC)': formatDateTime(sshKey.updatedAt),
                                    }}
                                />
                            ) : null}

                            <TextField
                                label={sshKey ? 'Replacement SSH public key' : 'SSH public key'}
                                value={publicKey}
                                onChange={(event) => {
                                    setPublicKey(event.target.value)
                                    setFileName(undefined)
                                }}
                                multiline
                                rows={4}
                                placeholder="ssh-ed25519, ssh-rsa, or ecdsa-sha2-nistp256…"
                                fullWidth
                                sx={{ '& textarea': { overflowY: 'auto' } }}
                            />
                            <Stack direction="row" spacing={1}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    disabled={saving}
                                >
                                    Upload .pub
                                    <input
                                        hidden
                                        type="file"
                                        accept=".pub,text/plain"
                                        aria-label="Upload SSH public key"
                                        onChange={(event) => void readFile(event)}
                                    />
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => void save()}
                                    disabled={!publicKey.trim() || saving}
                                >
                                    {saving ? 'Registering…' : sshKey ? 'Replace key' : 'Register key'}
                                </Button>
                                {sshKey && (
                                    <Button
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setRemoveDialogOpen(true)}
                                        disabled={saving}
                                    >
                                        Remove key
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                )}
                {errorBanner}
                {notice && <Banner id="notice" isOpen message={notice} onClose={() => setNotice(undefined)} />}
            </Stack>

            <Dialog open={removeDialogOpen} onClose={removing ? undefined : () => setRemoveDialogOpen(false)}>
                <DialogTitle>Remove SSH public key?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You will not be able to request new appliance SSH certificates until you register a key again.
                        Certificates already issued remain valid until they expire.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveDialogOpen(false)} disabled={removing}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={() => void remove()} disabled={removing}>
                        {removing ? 'Removing…' : 'Remove key'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Wrapper>
    )
}
